import * as THREE from "three";
import {ModelObjectStore, ShaderStore, TextureStore} from "./utils";
import {CameraEngine} from "./camera";
import {drawTex4} from "./shapes";

export class Entity {
    // Generates a unique-id
    createId() {
        let result, i, j;
        result = '';
        for (j = 0; j < 32; j++) {
            if (j == 8 || j == 12 || j == 16 || j == 20)
                result = result + '-';
            i = Math.floor(Math.random() * 16).toString(16).toUpperCase();
            result = result + i;
        }
        return result;
    }
    constructor(_scene) {
        console.log("Calling entity constructor");
        this.scene = _scene;
        this.root = null;
        this.actions = [];
        this.children = [];
        this.models = [];
        this.id = this.createId();
    }
    update(aclock) {
        if (this.texture != null) {
            this.uniforms.t0.value = TextureStore.getTexture(this.texture);
        }
        this.uniforms.time.value = aclock.dMS;
    }
}

export class ModelEntity extends Entity {
    constructor(scene, modelName, userData = {}, material = null) {
        super(scene);
        this.type = "model";
        this.mixer = null;
        this.material = null;
        this.uniforms = {
            time: {
                type: 'f',
                value: 1.0
            },
            mouse: {
                type: 'v2',
                value: new THREE.Vector2()
            },
            t0: {
                type: 't',
                value: TextureStore.getTexture('tile')
            }
        };
        let gltf = ModelObjectStore.getObject(modelName);
        this.parent = this.cloneAll(gltf);
        this.instance = this.parent.scene;
        userData['id'] = this.id;
        this.parent.scene.userData = userData;
        this.parent.scene.traverse( node => {
            node.userData = userData;
            if (node.isMesh) {
                // Iterate over textures
                // We can fully hijack the material here if we want.
                // but for the most part we can get by
                if (node.material && node.material.map) {
                    // Can we enable flat shading?
                    node.material.map.minFilter = THREE.NearestFilter;
                    node.material.map.magFilter = THREE.NearestFilter;
                    // We're going to need flat shading
                    // for anything decent with these peices.
                    if (material != null) {
                        node.material =
                            new THREE.MeshNormalMaterial(
                                { color: 0xdddddd,
                                    specular: 0x009900,
                                    shininess: 30,
                                    //map: texture,
                                    transparent: true
                                });
                    }
                }
                //console.log(node.material);

                // this generates materials

                // console.log(`${JSON.stringify(node)}`);
                //node.texture.minFilter = THREE.NearestFilter;
                //node.texture.magFilter = THREE.NearestFilter;
                //console.log(THREE.NearestFilter);
                //console.log(`parent type ${node.type}`);
            }

        });
        scene.add(this.parent.scene);
    }
    setScale(scale) {
        this.instance.scale.set(scale.x, scale.y, scale.z);
    }
    setPosition(pos) {
        this.instance.position.set(pos.x, pos.y, pos.z);
    }
    setRotation(pos) {
        this.instance.rotation.set(pos.x, pos.y, pos.z);
    }
    // Weird since we're working with buffer geometry for the most part.
    cloneStatic(gltf) {
    }
    cloneAll(gltf) {
        const clone = {
            animations: gltf.animations,
            scene: gltf.scene.clone(true)
        };
        const skinnedMeshes = {};
        gltf.scene.traverse(node => {
            if (node.isSkinnedMesh) {
                skinnedMeshes[node.name] = node;
            }
        });
        const cloneBones = {};
        const cloneSkinnedMeshes = {};
        clone.scene.traverse(node => {
            if (node.isBone) {
                cloneBones[node.name] = node;
            }
            if (node.isSkinnedMesh) {
                cloneSkinnedMeshes[node.name] = node;
            }
        });
        for (let name in skinnedMeshes) {
            const skinnedMesh = skinnedMeshes[name];
            const cloneSkinnedMesh = cloneSkinnedMeshes[name];
            let skeleton = skinnedMesh.skeleton;
            const orderedCloneBones = [];
            for (let i = 0; i < skeleton.bones.length; i++) {
                const cloneBone = cloneBones[skeleton.bones[i].name];
                orderedCloneBones.push(cloneBone);
            }
            // Weird that it's a skinned mesh?
            cloneSkinnedMesh.bind(
                new THREE.Skeleton(orderedCloneBones, skeleton.boneInverses),
                cloneSkinnedMesh.matrixWorld);
        }
        return clone;
    }
    // Set basic shader on the materials in a scene.
    setBasicShader() {

    }
    setShader(vs, fs) {
        const material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexColors: THREE.FaceColors,
            vertexShader: ShaderStore.get(vs),
            fragmentShader: ShaderStore.get(fs)
        });
        material.skinning = true;
        material.needsUpdate = true;
        this.scene.traverse((node) => {
            if (node.isMesh && node.material.skinning === true) {
                node.material = new THREE.ShaderMaterial({
                    uniforms: this.uniforms,
                    vertexColors: THREE.FaceColors,
                    vertexShader: ShaderStore.get(vs),
                    fragmentShader: ShaderStore.get(fs)
                });
                node.material.skinning = true;
                node.material.needsUpdate = true;
            }
        });
    }
}

export class FullScreenQuadEntity extends Entity {
    constructor(scene, camera, width, height, vs, fs) {
        super(scene);
        this.uniforms = {
            time: {
                type: 'f',
                value: 1.0
            },
            resolution: {
                type: 'v2',
                value: new THREE.Vector2(width, height)
            },
            mouse: {
                type: 'v2',
                value: new THREE.Vector2()
            },
            t0: {
                type: 't',
                value: TextureStore.getTexture('flower')
            }
        };
        this.camera = camera;
        this.material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexColors: THREE.FaceColors,
            vertexShader: ShaderStore.get(vs),
            fragmentShader: ShaderStore.get(fs),
            blending: THREE.NormalBlending,
            depthTest: true,
            transparent: true
        });
        this.cameraEngine = new CameraEngine();
        this.cameraEngine.compute(this.camera);
        this.bgPlane = drawTex4(
            this.cameraEngine.farBottomLeft.x,
            this.cameraEngine.farBottomLeft.y,
            this.cameraEngine.farBottomLeft.z,
            this.cameraEngine.farBottomRight.x,
            this.cameraEngine.farBottomRight.y,
            this.cameraEngine.farBottomRight.z,
            this.cameraEngine.farTopLeft.x,
            this.cameraEngine.farTopLeft.y,
            this.cameraEngine.farTopLeft.z,
            this.cameraEngine.farTopRight.x,
            this.cameraEngine.farTopRight.y,
            this.cameraEngine.farTopRight.z,
            this.material
        );
        this.bgPlane.userData = "BG";
        this.scene.add(this.bgPlane);
    }
    setTexture(textureName) {
        this.texture = textureName;
    }
    recompute() {

        // recompute the bounds
    }
    update(aclock) {
        if (this.texture != null) {
            this.uniforms.t0.value = TextureStore.getTexture(this.texture);
        }
        this.uniforms.time.value = aclock.dMS;
    }
}

export class QuadEntity extends Entity {
    constructor(scene, width, height, vs, fs) {
        super(scene);
        // in case a additional render hook needs to be added.
        this.extra = function () {
        };

        this.uniforms = {
            time: {
                type: 'f',
                value: 1.0
            },
            resolution: {
                type: 'v2',
                value: new THREE.Vector2(width, height)
            },
            mouse: {
                type: 'v2',
                value: new THREE.Vector2()
            },
            t0: {
                type: 't',
                value: TextureStore.getTexture('flower')
            }
        };
        this.material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexColors: THREE.FaceColors,
            vertexShader: ShaderStore.get(vs),
            fragmentShader: ShaderStore.get(fs),
            blending: THREE.NormalBlending,
            depthTest: true,
            transparent: true
        });
        let bottomLeft = new THREE.Vector3(-width / 2, -height / 2, 0.0);
        let bottomRight = new THREE.Vector3(width / 2, -height / 2, 0);
        let topLeft = new THREE.Vector3(-width / 2, height / 2, 0.0);
        let topRight = new THREE.Vector3(width / 2, height / 2, 0.0);
        this.quad = drawTex4(
            bottomLeft.x, bottomLeft.y, bottomLeft.z,
            bottomRight.x, bottomRight.y, bottomRight.z,
            topLeft.x, topLeft.y, topLeft.z,
            topRight.x, topRight.y, topRight.z,
            this.material
        );
        this.quad.userData = "QUAD";
        this.scene.add(this.quad);
    }
    setPosition(pos) {
        this.quad.position.set(pos.x, pos.y, pos.z);
    }
    setTexture(textureName) {
        this.texture = textureName;
    }
    recompute() {
    }
    update(aclock) {
        if (this.texture != null) {
            this.uniforms.t0.value = TextureStore.getTexture(this.texture);
        }
        this.uniforms.time.value = aclock.dMS;
    }
}
